/** @jsxImportSource @emotion/react */

import * as React from 'react'
import { jsx, css } from '@emotion/react'
import { Popper } from 'react-popper'
import styled from '@emotion/styled'
import { ClassNames } from '@emotion/react'
import { CSSInterpolation } from '@emotion/serialize'
import { Primary, mixins, shadows } from '@sphinx/components-styles'
import { Placement } from '../../'
import { useClickOutside } from '../../hooks/useClickOutside'

const arrowUpKeyCode = 38
const arrowDownKeyCode = 40

export enum ForceToggleType {
  CloseAndBlur = 'CloseAndBlur',
  Open = 'Open',
  Close = 'Close',
  Default = 'Default',
}

interface TooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  placement?: Placement
  disabled?: boolean
  trigger: React.ReactElement
  activeStyle?: string
  onToggled?: (isDropDownActive: boolean) => void
  forceToggle?: ForceToggleType
  menuWrapDataE2E?: string
}

export const DropdownButton = styled.button`
  ${mixins.menu}
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  box-sizing: border-box;
  outline: none;
  cursor: pointer;
  color: ${Primary.navy[100]};
  border: 1px solid ${Primary.navy[20]};
  background: ${Primary.navy[5]};
  border-radius: 2px;

  &:hover {
    border: 1px solid ${Primary.navy[100]};
  }
  &:focus {
    border: 1px solid ${Primary.orange[100]};
    box-shadow: inset 1px 1px ${Primary.orange[100]},
      inset -1px -1px ${Primary.orange[100]};
  }
  &:disabled {
    cursor: default;
    pointer-events: none;
    border: 1px solid transparent;
    color: ${Primary.navy[20]};
    background: ${Primary.navy[5]};
    & path {
      fill: ${Primary.navy[20]};
    }
  }
  &:active {
    color: ${Primary.orange[100]};
    border: 1px solid ${Primary.orange[100]};
    box-shadow: none;
    & path {
      fill: ${Primary.orange[100]};
    }
  }
  & path {
    fill: ${Primary.navy[100]};
  }
  & > :first-of-type ~ * {
    margin-left: 8px;
  }
  & > :first-of-type ~ svg {
    margin-left: 3px;
  }
  & > svg {
    margin: 0 -5px;
  }
`

const defaultActiveStyle = `
  color: ${Primary.orange[100]};
  border: 1px solid ${Primary.orange[100]};
  &:hover {
    border: 1px solid ${Primary.orange[100]};
  }
  & path {
    fill: ${Primary.orange[100]};
  }
`

const optionStyles = `
  ${mixins.menu}
  color: ${Primary.navy[100]};
  background-color: ${Primary.neutral.white};
  border: 1px solid transparent;
  width: 100%;
  height: 36px;
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 6px 8px;

  &:active&:focus, &:active&:hover {
    background-color: ${Primary.orange[20]};
  }
  &:hover {
    background-color: ${Primary.navy[5]};
  }
  &:focus {
    border: 1px solid ${Primary.orange[100]};
  }
`

const optionDisabledStyle = `
  cursor: default;
  background-color: transparent;
  color: ${Primary.navy[20]};
`

export const OptionButton = styled.button`
  ${optionStyles}
  &:disabled {
    ${optionDisabledStyle}
  }
`

export const OptionLink = styled.a<{ disabled?: boolean }>`
  ${optionStyles}
  box-sizing: border-box;
  text-decoration: none;
  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      ${optionDisabledStyle}
    `}
`

const MenuWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px 0;
  box-shadow: ${shadows.level8};
  border-radius: 2px;
  overflow: hidden;
  background: ${Primary.neutral.white};
`

export const Dropdown: React.FC<TooltipProps> = ({
  placement: propsPlacement = Placement.bottomStart,
  trigger,
  children,
  disabled,
  style: dropdownStyle,
  activeStyle = defaultActiveStyle,
  onToggled = () => {},
  forceToggle = ForceToggleType.Default,
  ...props
}) => {
  const referenceElement = React.useRef(null)
  const wrapperRef = React.useRef(null)
  const [open, setOpen] = React.useState(false)
  const options = [...new Array(React.Children.count(children))]
  const [activeFocusableBtn, setActiveFocusableBtn] = React.useState(-1)

  const toggle = () => setOpen(!open)

  const countLimit = (current: number, max: number, min = 0) =>
    Math.max(min, Math.min(current, max))

  const handleItemFocus = (index: number) => {
    const focusable = options.reduce(
      (acc, item, i) =>
        item && item.hasAttribute('disabled') ? acc : [...acc, i],
      []
    )
    const number = countLimit(index, focusable.length - 1)
    setActiveFocusableBtn(number)
    options[focusable[number]].focus()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.keyCode === arrowUpKeyCode && options[0]) {
      handleItemFocus(activeFocusableBtn - 1)
    }
    if (e.keyCode === arrowDownKeyCode && options[0]) {
      handleItemFocus(activeFocusableBtn + 1)
    }
  }

  const onFocusHandle = (i: number) => () => setActiveFocusableBtn(i)

  const getTriggerProps = (
    stylization: (
      template: TemplateStringsArray,
      ...args: Array<CSSInterpolation>
    ) => string
  ) => {
    const commonProps = {
      disabled,
      ref: referenceElement,
      onClick: (e: React.MouseEvent) => {
        if (trigger.props.onClick) {
          trigger.props.onClick(e)
        }
        toggle()
      },
    }

    if (open) {
      return {
        ...commonProps,
        className: stylization`
          ${activeStyle}
        `,
      }
    }

    return commonProps
  }

  useClickOutside(wrapperRef, () => setOpen(false))

  React.useLayoutEffect(() => {
    wrapperRef.current.focus()
    setActiveFocusableBtn(-1)
  }, [open])

  React.useEffect(() => {
    onToggled(open)
  }, [open])

  React.useEffect(() => {
    switch (forceToggle) {
      case ForceToggleType.Close:
        setOpen(false)
        break
      case ForceToggleType.Open:
        setOpen(true)
        break
      case ForceToggleType.CloseAndBlur:
        setOpen(false)
        referenceElement.current?.blur()
        break
      case ForceToggleType.Default:
      default:
        break
    }
  }, [forceToggle])

  return (
    <div
      style={{ ...dropdownStyle, display: 'inline-block' }}
      onKeyDown={handleKeyPress}
      ref={wrapperRef}
      {...props}
    >
      <ClassNames>
        {({ css: stylization }) =>
          React.cloneElement(trigger, getTriggerProps(stylization))
        }
      </ClassNames>
      {open && !disabled && (
        <Popper
          modifiers={{
            flip: {
              behavior: [Placement.bottom, Placement.top],
            },
          }}
          positionFixed
          placement={propsPlacement}
          referenceElement={referenceElement.current}
        >
          {({ ref, style, placement }) => (
            <MenuWrap
              data-placement={placement}
              style={{
                ...style,
                minWidth:
                  referenceElement.current.getBoundingClientRect().width,
              }}
              ref={ref}
              data-e2e={props.menuWrapDataE2E}
            >
              {React.Children.map(children, (child: any, i) =>
                React.cloneElement(child, {
                  onFocus: (e: FocusEvent) => {
                    if (child.props.onFocus) {
                      child.props.onFocus(e)
                    }
                    onFocusHandle(i)
                  },
                  onMouseDown: (e: FocusEvent) => {
                    e.preventDefault()
                    if (child.props.onMouseDown) {
                      child.props.onMouseDown(e)
                    }
                  },
                  onClick: (e: FocusEvent) => {
                    if (child.props.onClick) {
                      child.props.onClick(e)
                    }
                    toggle()
                  },
                  tabIndex: child.props.disabled ? -1 : 0,
                  ref: (reference: any) => (options[i] = reference),
                })
              )}
            </MenuWrap>
          )}
        </Popper>
      )}
    </div>
  )
}
